import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type ToolbarMode = 'full' | 'compact' | 'minimal';

export interface ResponsiveToolbarState {
  mode: ToolbarMode;
  showPip: boolean;
  toolbarRef: React.RefObject<HTMLDivElement>;
  isReady: boolean;
}

interface ModeThresholds {
  compactThreshold: number;
  minimalThreshold: number;
  pipThreshold: number;
  hysteresis: number;
}

const DEFAULT_THRESHOLDS: ModeThresholds = {
  compactThreshold: 1020,  // Trigger compact when toolbar content (~1008px) overflows
  minimalThreshold: 700,   // Trigger minimal at ~700px available container width
  pipThreshold: 800,       // Hide PiP button below ~800px
  hysteresis: 30,
};

const DEBUG = false;

function debugLog(message: string, data?: Record<string, unknown>) {
  if (DEBUG) {
    console.log(`[ToolbarModes] ${message}`, data ? data : '');
  }
}

export function useResponsiveToolbarModes(
  thresholds: Partial<ModeThresholds> = {}
): ResponsiveToolbarState {
  const mergedThresholds = useMemo(
    () => ({ ...DEFAULT_THRESHOLDS, ...thresholds }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [thresholds.compactThreshold, thresholds.minimalThreshold, thresholds.pipThreshold, thresholds.hysteresis]
  );
  
  const toolbarRef = useRef<HTMLDivElement>(null);
  
  const [mode, setMode] = useState<ToolbarMode>('full');
  const [showPip, setShowPip] = useState(true);
  const [isReady, setIsReady] = useState(false);
  
  const rafIdRef = useRef<number | null>(null);
  const lastModeRef = useRef<ToolbarMode>('full');
  const lastShowPipRef = useRef<boolean>(true);
  const lastWidthRef = useRef<number>(1000);
  const isUpdatingRef = useRef<boolean>(false);

  const calculateMode = useCallback(() => {
    debugLog('calculateMode called');
    
    if (isUpdatingRef.current) {
      debugLog('Skipping - update in progress');
      return;
    }
    
    const toolbar = toolbarRef.current;
    if (!toolbar) {
      debugLog('No toolbar ref found - ref is null');
      return;
    }

    // Log DOM structure for debugging
    const closestContainer = toolbar.closest('.recording-controls-container');
    const parentEl = toolbar.parentElement;
    const grandparentEl = parentEl?.parentElement;
    
    debugLog('DOM structure analysis', {
      toolbarClass: toolbar.className,
      closestContainerFound: !!closestContainer,
      closestContainerClass: closestContainer?.className || 'N/A',
      closestContainerWidth: (closestContainer as HTMLElement)?.clientWidth || 'N/A',
      parentClass: parentEl?.className || 'N/A',
      parentTag: parentEl?.tagName || 'N/A',
      parentWidth: parentEl?.clientWidth || 'N/A',
      grandparentClass: grandparentEl?.className || 'N/A',
      grandparentWidth: grandparentEl?.clientWidth || 'N/A',
      windowWidth: window.innerWidth
    });

    let container = closestContainer as HTMLElement;
    
    if (!container) {
      container = parentEl as HTMLElement;
      debugLog('Fallback to parentElement', { 
        parentClass: container?.className,
        parentTag: container?.tagName,
        parentWidth: container?.clientWidth
      });
    }
    
    if (!container) {
      debugLog('No container found at all');
      return;
    }

    const containerStyle = getComputedStyle(container);
    const paddingLeft = parseFloat(containerStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(containerStyle.paddingRight) || 0;
    const containerClientWidth = container.clientWidth;
    const availableWidth = containerClientWidth - paddingLeft - paddingRight;

    const currentMode = lastModeRef.current;

    debugLog('Width measurements', {
      containerClientWidth,
      paddingLeft,
      paddingRight,
      availableWidth
    });

    const widthDiff = Math.abs(availableWidth - lastWidthRef.current);
    if (widthDiff < 5 && isReady) {
      debugLog('Width change too small, skipping', { widthDiff, lastWidth: lastWidthRef.current });
      return;
    }
    lastWidthRef.current = availableWidth;

    const { compactThreshold, minimalThreshold, pipThreshold, hysteresis } = mergedThresholds;

    debugLog('Thresholds', {
      compactThreshold,
      minimalThreshold,
      pipThreshold,
      hysteresis,
      currentMode,
      availableWidth
    });

    // Determine mode purely based on available container width
    // This avoids issues with scrollWidth measurements being unstable during UI animations
    let newMode: ToolbarMode = 'full';
    
    if (availableWidth < minimalThreshold) {
      newMode = 'minimal';
    } else if (availableWidth < compactThreshold) {
      newMode = 'compact';
    } else {
      newMode = 'full';
    }
    
    // Apply hysteresis to prevent mode flickering at threshold boundaries
    if (currentMode === 'compact' && newMode === 'full') {
      if (availableWidth < compactThreshold + hysteresis) {
        newMode = 'compact';
      }
    }
    if (currentMode === 'minimal' && newMode !== 'minimal') {
      if (availableWidth < minimalThreshold + hysteresis) {
        newMode = 'minimal';
      }
    }

    debugLog('Mode calculation', {
      currentMode,
      newMode,
      availableWidth
    });

    const currentShowPip = lastShowPipRef.current;
    let newShowPip: boolean;
    if (currentShowPip) {
      newShowPip = availableWidth >= pipThreshold - hysteresis;
    } else {
      newShowPip = availableWidth >= pipThreshold + hysteresis;
    }

    debugLog('Mode decision', {
      currentMode,
      newMode,
      currentShowPip,
      newShowPip,
      modeChanged: newMode !== lastModeRef.current,
      pipChanged: newShowPip !== lastShowPipRef.current
    });

    if (newMode !== lastModeRef.current || newShowPip !== lastShowPipRef.current) {
      isUpdatingRef.current = true;
      
      if (newMode !== lastModeRef.current) {
        debugLog('Setting new mode', { from: lastModeRef.current, to: newMode });
        lastModeRef.current = newMode;
        setMode(newMode);
      }
      
      if (newShowPip !== lastShowPipRef.current) {
        debugLog('Setting showPip', { from: lastShowPipRef.current, to: newShowPip });
        lastShowPipRef.current = newShowPip;
        setShowPip(newShowPip);
      }

      setTimeout(() => {
        isUpdatingRef.current = false;
        debugLog('Update cooldown complete');
      }, 100);
    }

    if (!isReady) {
      debugLog('Hook is now ready');
      setIsReady(true);
    }
  }, [mergedThresholds, isReady]);

  const debouncedCalculate = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }
    rafIdRef.current = requestAnimationFrame(calculateMode);
  }, [calculateMode]);

  useEffect(() => {
    debugLog('useEffect running');
    
    const toolbar = toolbarRef.current;
    if (!toolbar) {
      debugLog('useEffect: No toolbar ref yet - waiting for next render');
      return;
    }

    debugLog('useEffect: toolbar ref found', { 
      toolbarClass: toolbar.className,
      toolbarClientWidth: toolbar.clientWidth
    });

    let container = toolbar.closest('.recording-controls-container') as HTMLElement;
    if (!container) {
      container = toolbar.parentElement as HTMLElement;
      debugLog('useEffect: Using parentElement as container', { 
        className: container?.className,
        clientWidth: container?.clientWidth
      });
    } else {
      debugLog('useEffect: Found .recording-controls-container', {
        className: container.className,
        clientWidth: container.clientWidth
      });
    }
    
    if (!container) {
      debugLog('useEffect: No container found, calling calculate anyway');
      debouncedCalculate();
      return;
    }

    debugLog('useEffect: Setting up ResizeObserver on container', { 
      className: container.className,
      clientWidth: container.clientWidth,
      offsetWidth: container.offsetWidth,
      scrollWidth: container.scrollWidth
    });

    const resizeObserver = new ResizeObserver((entries) => {
      debugLog('ResizeObserver triggered', { 
        width: entries[0]?.contentRect.width,
        height: entries[0]?.contentRect.height
      });
      debouncedCalculate();
    });

    resizeObserver.observe(container);
    
    // Initial calculation
    debugLog('useEffect: Running initial debouncedCalculate');
    debouncedCalculate();

    return () => {
      debugLog('Cleaning up ResizeObserver');
      resizeObserver.disconnect();
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [debouncedCalculate]);

  return {
    mode,
    showPip,
    toolbarRef,
    isReady,
  };
}

export default useResponsiveToolbarModes;
